<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps<{
  visible: boolean
  uploading: boolean
}>()

const emit = defineEmits<{
  upload: [file: File]
  close: []
}>()

const file = ref<File | null>(null)
const preview = ref<string | null>(null)

watch(
  () => props.visible,
  (v) => {
    if (!v) {
      file.value = null
      if (preview.value) {
        URL.revokeObjectURL(preview.value)
      }
      preview.value = null
    }
  },
)

function onFileChange(files: File | File[] | null) {
  const f = Array.isArray(files) ? files[0] : files
  file.value = f ?? null
  if (preview.value) {
    URL.revokeObjectURL(preview.value)
  }
  if (f) {
    preview.value = URL.createObjectURL(f)
  } else {
    preview.value = null
  }
}

onBeforeUnmount(() => {
  if (preview.value) {
    URL.revokeObjectURL(preview.value)
  }
})

function handleUpload() {
  if (!file.value) {
    return
  }
  emit('upload', file.value)
}
</script>

<template>
  <v-dialog max-width="600" :model-value="visible" persistent @keydown.esc="emit('close')">
    <v-card>
      <v-card-title>Upload Torah Column Photo</v-card-title>
      <v-card-text>
        <v-file-input
          accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
          :disabled="uploading"
          label="Torah column photo"
          prepend-icon="mdi-camera"
          @update:model-value="onFileChange"
        />

        <v-img v-if="preview" class="my-3" max-height="300" :src="preview" />

        <v-alert v-if="uploading" class="mt-3" density="compact" type="info">
          <template #prepend>
            <v-progress-circular indeterminate size="20" width="2" />
          </template>
          Analyzing the photo… This may take a moment.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="uploading" variant="plain" @click="emit('close')">Cancel</v-btn>
        <v-btn color="primary" :disabled="!file || uploading" @click="handleUpload">Upload & Analyze</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
